/**
 * Conveyor — a belt solid (Brief 011 kinematic-solid family).
 *
 * A (usually static) SOLID the player can stand on like a tile (reuses the Brief 009
 * solid spine), which additionally PUSHES a grounded rider sideways: World displaces
 * the rider by `surfaceVx() * dt` each step while standing on its top. `props.surfaceSpeed`
 * is signed (positive = right). The rider can still walk against the belt — the belt
 * displacement and the player's own input simply add, giving the classic "running on a
 * treadmill" feel.
 *
 * Determinism: the belt is a constant px/s applied over the fixed step — no transcendentals,
 * no wall-clock. `facing` mirrors the belt direction so the chevron art points the right way.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

const DEFAULT_SURFACE_SPEED = 120; // px/s, signed
const DEFAULT_W_TILES = 2;
const DEFAULT_H_TILES = 1;

function numProp(props: Record<string, unknown> | undefined, key: string, fallback: number): number {
  const v = props?.[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

export class Conveyor implements Entity {
  readonly type = EntityType.CONVEYOR;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;

  private readonly surfaceSpeed: number;

  constructor(cx: number, feetY: number, props: Record<string, unknown> | undefined, ts: number) {
    const w = Math.max(1, numProp(props, 'w', DEFAULT_W_TILES)) * ts;
    const h = Math.max(1, numProp(props, 'h', DEFAULT_H_TILES)) * ts;
    this.body = { x: cx - w / 2, y: feetY - h, w, h };
    this.surfaceSpeed = numProp(props, 'surfaceSpeed', DEFAULT_SURFACE_SPEED);
    this.facing = this.surfaceSpeed < 0 ? -1 : 1; // flip the chevron art to the belt direction
  }

  solidBody(): AABB { return this.body; }

  update(_dt: number, _ctx: EntityContext): void { /* static belt — no motion */ }

  /** Horizontal velocity imparted to a rider standing on top (Brief 011). */
  surfaceVx(): number { return this.surfaceSpeed; }

  onPlayerOverlap(_ctx: EntityContext): void { /* solid — interaction is via standing */ }

  spriteKey(): string { return 'conveyor'; }
  visible(): boolean { return true; }
  renderSize(): { w: number; h: number } { return { w: this.body.w, h: this.body.h }; }
}
